import React from 'react';

/**
 * Working paper dialog registry.
 *
 * Registered dialogs should resolve wpStatus for issue-mark save via
 * resolveInitialWpStatus(currentData?.wpStatus, payload?.line?.wpStatus)
 * (see wpStatusSaveHelper.ts) after the Facade query merge.
 *
 * Issue mark Switch (WpIssueMarkControl) is always rendered in WpCheckToolbar /
 * WpPerCheckSection alongside the dynamic WP component (no visibility intercept).
 *
 * ## Adjustment 组装 / 渲染路径
 *
 * | 组件 | FS codes | 路径 |
 * |------|----------|------|
 * | DueFromToDirectorsWp | `BS-CL-DTD` | **special 全局**（`SPECIAL_ADJUST_GLOBAL_CODES`，originalEntry 失败默认 Cr） |
 * | DividendWp | `BS-EQ-DIV` | **special 全局**（`SPECIAL_ADJUST_GLOBAL_CODES`，originalEntry 失败默认 Dr） |
 * | TaxWp | `BS-CA-TAXR` / `BS-CL-TAXP`（Tax b/f） | **special scoped**（`SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES` + 运行时 `specialDtdPathCodes`） |
 * | DueFromToDirectorsWp | `BS-CA-DIR` | **非 special**（FsCategory originalEntry 常规规则） |
 * | paper-registry 中其余所有组件 | 各自 code | **非 special**（除非加入全局/scoped 集合并注册） |
 *
 * 入口判定：`usesSpecialAdjustPathScoped(code, specialDtdPathCodes)`
 * （兼容别名 `usesDtdAdjustmentPathScoped`）。扩接入见 specialAdjustFlow 文件头说明。
 */
export {resolveInitialWpStatus} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpStatusSaveHelper';
export {DTD_CL_CODE as DTD_ADJUSTMENT_PATH_CODE, usesDtdAdjustmentPath} from '@/adjustment/adjust/utils/specialAdjustFlow';

/** DueFromToDirectorsWp 注册的 FS code（共享同一 WP 组件，adjustment 路径不同） */
export const DUE_FROM_TO_DIRECTORS_FS_CODES = ['BS-CA-DIR', 'BS-CL-DTD'] as const;

// 新的 code 映射（优先使用）- 基于最新 API 返回的数据
const codeRegistry: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
    // ======= Balance Sheet - Non-Current Assets (BS-NCA) =======
    'BS-NCA-PPE': React.lazy(() => import('@/wp/asset/PropertyPlantEquipment')),
    'BS-NCA-IA': React.lazy(() => import('@/wp/asset/IntangibleAssetsWp')),
    'BS-NCA-ITL': React.lazy(() => import('@/wp/asset/TaxiLicensesWp')),
    // G1,G2
    'BS-NCA-ISUB': React.lazy(() => import('@/wp/basic/CommonInvestmentWp')),
    'BS-NCA-IASS': React.lazy(() => import('@/wp/basic/CommonInvestmentWp')),
    
    // ======= Balance Sheet - Current Assets (BS-CA) =======
    'BS-CA-IOT': React.lazy(() => import('@/wp/basic/CommonInvestmentWp')),
    'BS-CA-INV': React.lazy(() => import('@/wp/asset/InventoriesWp')),
    'BS-CA-ORC': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DPP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-PPM': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-TR': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DIMH': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DIR': React.lazy(() => import('@/wp/dynamic/DueFromToDirectorsWp')),
    'BS-CA-DSS': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DRP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DRP2': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-DRP3': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CA-CASH': React.lazy(() => import('@/wp/basic/BankWp')),
    'BS-CA-PND': React.lazy(() => import('@/wp/basic/CommonWp')),
    
    // ======= Balance Sheet - Current Liabilities (BS-CL) =======
    'BS-CL-TP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-OP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DR': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-ACC': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-BOD': React.lazy(() => import('@/wp/basic/BankWp')),
    'BS-CL-BLS': React.lazy(() => import('@/wp/loan/BankLoan')),
    'BS-CL-OFL': React.lazy(() => import('@/wp/loan/VehicleLoan')),
    'BS-CL-DTS': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DTSH': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DTRC': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DRP': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DRP2': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DRP3': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-CL-DTD': React.lazy(() => import('@/wp/dynamic/DueFromToDirectorsWp')),
    'BS-CL-RND': React.lazy(() => import('@/wp/basic/CommonWp')),
    
    // ======= Balance Sheet - Non-Current Liabilities (BS-NCL) =======
    'BS-NCL-ONI': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-NCL-BLS': React.lazy(() => import('@/wp/loan/BankLoan')),
    'BS-NCL-OFL': React.lazy(() => import('@/wp/loan/VehicleLoan')),
    
    // ======= Balance Sheet - Equity (BS-EQ) =======
    'BS-EQ-IC': React.lazy(() => import('@/wp/equity/IssuedCapital')),
    'BS-EQ-AL': React.lazy(() => import('@/wp/basic/CommonWp')),
    'BS-EQ-DIV': React.lazy(() => import('@/wp/equity/DividendWp')),
    'BS-EQ-OFFS': React.lazy(() => import('@/wp/basic/CommonWp')),
    
    // ======= Profit & Loss - Turnover (PL-TURN) =======
    'PL-TURN-SI': React.lazy(() => import('@/wp/income/SalesIncomeWp')),
    'PL-TURN-SE': React.lazy(() => import('@/wp/income/SalesIncomeWp')),
    'PL-TURN-RI': React.lazy(() => import('@/wp/income/RentalIncomeWp')),
    
    // ======= Profit & Loss - Cost of Turnover (PL-COT) =======
    'PL-COT-OI': React.lazy(() => import('@/wp/asset/InventoriesWp')),
    'PL-COT-PUR': React.lazy(() => import('@/wp/expenses/PurchasesWp')),
    'PL-COT-CI': React.lazy(() => import('@/wp/asset/InventoriesWp')),
    /** Registry key for company FS custom nodes (templateCodeRef). */
    'CommonTransactionWp': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    'PL-COT-ROY': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    'PL-COT-SUB': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-COT-OCT': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    
    // ======= Profit & Loss - Other Income (PL-OI) =======
    'PL-OI-II': React.lazy(() => import('@/wp/income/InterestIncomeWp')),
    'PL-OI-SFG': React.lazy(() => import('@/wp/income/InterestIncomeWp')),
    'PL-OI-DI': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    'PL-OI-EXG': React.lazy(() => import('@/wp/dynamic/GainLossExchangeWp')),
    'PL-OI-ALI': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    'PL-OI-GOD': React.lazy(() => import('@/wp/dynamic/GainLossDisposalWp')),
    'PL-OI-SI': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    
    // ======= Profit & Loss - Administrative Expenses (PL-AE) =======
    'PL-AE-ACF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-ADM': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-ADP': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-AR': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-AOP': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-BC': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-BMF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-BRF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-BD': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-PDD': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-CER': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CLE': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CMP': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CSE': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-COM': React.lazy(() => import('@/wp/expenses/CommissionPaymentWp')),
    'PL-AE-CF': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-CONS': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CUR': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CRR': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-CUD': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-DSN': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-DEP': React.lazy(() => import('@/wp/expenses/DepreciationSummaryWp')),
    'PL-AE-AIA': React.lazy(() => import('@/wp/asset/IntangibleAssetsWp')), // Amortization linked to Intangible assets
    'PL-AE-DR': React.lazy(() => import('@/wp/expenses/DirectorSalaryWp')),
    'PL-AE-DQ': React.lazy(() => import('@/wp/expenses/DirectorSalaryWp')),
    'PL-AE-DON': React.lazy(() => import('@/wp/expenses/DonationWp')),
    'PL-AE-EW': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-ENT': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-EXL': React.lazy(() => import('@/wp/dynamic/GainLossExchangeWp')),
    'PL-AE-EXH': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-FRA': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-FRG': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-GAS': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-HOT': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-IML': React.lazy(() => import('@/wp/basic/CommonWp')),
    'PL-AE-INS': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-IPP': React.lazy(() => import('@/wp/basic/CommonTransactionWp')),
    'PL-AE-LPF': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-LIC': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-LTR': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-LOD': React.lazy(() => import('@/wp/dynamic/GainLossDisposalWp')),
    'PL-AE-MGT': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-MED': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-MEM': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-MVE': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-MPF': React.lazy(() => import('@/wp/expenses/MpfSummaryWp')),
    'PL-AE-MES': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-OFF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-OTR': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-PEN': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-PE': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-PS': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-PAF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-REN': React.lazy(() => import('@/wp/expenses/RentalExpenseWp')),
    'PL-AE-RAM': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SAL': React.lazy(() => import('@/wp/expenses/SalariesWageWp')),
    'PL-AE-SAM': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SCF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SWF': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SD': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')), // STAMP_DUTIES
    'PL-AE-STO': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SUB': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-SUE': React.lazy(() => import('@/wp/basic/DebtFeesTransactionWp')),
    'PL-AE-SUN': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-TEL': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-TRN': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-TRV': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-TP': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-UTL': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-VO': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-WAG': React.lazy(() => import('@/wp/expenses/SalariesWageWp')),
    'PL-AE-WSE': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-AE-WHT': React.lazy(() => import('@/wp/expenses/WithholdingTaxWp')),
    
    // ======= Profit & Loss - Finance Cost (PL-FC) =======
    'PL-FC-LI': React.lazy(() => import('@/wp/loan/InterestSummaryWp')),
    'PL-FC-INT': React.lazy(() => import('@/wp/basic/CommonMaterialTestWp')),
    'PL-FC-BC': React.lazy(() => import('@/wp/basic/BankWp')),

    // ======= Balance Sheet - Tax =======
    'BS-CA-TAXR': React.lazy(() => import('@/wp/tax/TaxWp')),
    'BS-CL-TAXP': React.lazy(() => import('@/wp/tax/TaxWp')),
    // ======= Profit & Loss - Tax =======
    'PL-TAX': React.lazy(() => import('@/wp/tax/TaxWp')),
};

// 合并两个 registry，优先使用 code 映射
export const AdjustDialogRegistry: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
    ...codeRegistry,
};

/**
 * Resolve dialog component for a FS code.
 * PL-AE-DEP is shared by PPE and ITL — when wpFetchCode is Taxi/ITL, use TaxiLicensesWp.
 */
export function resolveAdjustDialogComponent(
    code?: string | null,
    wpFetchCode?: string | null,
): React.LazyExoticComponent<React.ComponentType<any>> | undefined {
    const upper = String(code || '').trim().toUpperCase();
    const fetch = String(wpFetchCode || '').trim().toUpperCase();
    if (
        upper === 'PL-AE-DEP'
        && (fetch === 'BS-NCA-ITL' || fetch === 'TAXILICENSE' || fetch === 'TAXI_LICENSE')
    ) {
        return AdjustDialogRegistry['BS-NCA-ITL'];
    }
    return AdjustDialogRegistry[upper] ?? AdjustDialogRegistry[String(code || '').trim()];
}

// Export keys and a helper to get all registered dialog keys
export type AdjustDialogKey = keyof typeof AdjustDialogRegistry;
export const AdjustDialogKeys = Object.keys(AdjustDialogRegistry) as AdjustDialogKey[];
